// config/integrationsConfig.js - Comprehensive Integration Configuration

// Enhanced validation functions
export const validateIntegrationConfigInternal = (config) => {
    if (!config || typeof config !== 'object') {
        throw new Error('Integration config must be a valid object');
    }

    const requiredFields = ['name', 'type', 'status'];
    const validTypes = ['CRM', 'HR', 'EMAIL', 'SAML', 'CALENDAR', 'MARKETING', 'ANALYTICS', 'COMMUNICATION'];
    const validStatuses = ['active', 'inactive', 'coming_soon', 'beta', 'deprecated'];

    if (!requiredFields.every(field => config.hasOwnProperty(field))) {
        throw new Error(`Integration config missing required fields: ${requiredFields.join(', ')}`);
    }

    if (!validTypes.includes(config.type)) {
        throw new Error(`Invalid integration type. Valid types: ${validTypes.join(', ')}`);
    }

    if (!validStatuses.includes(config.status)) {
        throw new Error(`Invalid integration status. Valid statuses: ${validStatuses.join(', ')}`);
    }

    return config;
};

const validateApiConfig = (apiConfig) => {
    if (!apiConfig || typeof apiConfig !== 'object') {
        throw new Error('API config must be a valid object');
    }

    if (apiConfig.baseUrl && typeof apiConfig.baseUrl !== 'string') {
        throw new Error('API base URL must be a string');
    }

    if (apiConfig.timeout && (typeof apiConfig.timeout !== 'number' || apiConfig.timeout < 1000)) {
        throw new Error('API timeout must be a number greater than 1000ms');
    }

    return apiConfig;
};

// Enhanced error handler
const handleIntegrationError = (error, operation = 'Integration operation') => {
    console.error(`[IntegrationsConfig] ${operation} Error:`, {
        message: error.message,
        stack: error.stack,
        operation
    });

    throw new Error(`Integration configuration error: ${error.message}`);
};

// Comprehensive integration configuration
export const INTEGRATIONS_CONFIG = {
    CRM: [
        {
            name: 'Salesforce',
            type: 'CRM',
            status: 'active',
            description: 'Leading CRM platform for sales, service, and marketing',
            icon: 'salesforce-icon.svg',
            apiConfig: {
                baseUrl: process.env.REACT_APP_SALESFORCE_API_URL,
                timeout: 30000,
                version: 'v58.0',
                authType: 'oauth2'
            },
            features: ['contact_sync', 'lead_management', 'opportunity_tracking', 'reporting'],
            webhookSupport: true,
            rateLimits: { requests: 2000, window: '24h' },
            documentation: 'https://developer.salesforce.com/docs'
        },
        {
            name: 'HubSpot',
            type: 'CRM',
            status: 'active',
            description: 'Inbound marketing and sales platform',
            icon: 'hubspot-icon.svg',
            apiConfig: {
                baseUrl: 'https://api.hubapi.com',
                timeout: 30000,
                version: 'v3',
                authType: 'api_key'
            },
            features: ['contact_management', 'deal_pipeline', 'email_marketing', 'analytics'],
            webhookSupport: true,
            rateLimits: { requests: 100000, window: '24h' },
            documentation: 'https://developers.hubspot.com/docs'
        },
        {
            name: 'Zoho CRM',
            type: 'CRM',
            status: 'active',
            description: 'Cloud-based CRM solution for businesses',
            icon: 'zoho-icon.svg',
            apiConfig: {
                baseUrl: 'https://www.zohoapis.com/crm',
                timeout: 30000,
                version: 'v2',
                authType: 'oauth2'
            },
            features: ['lead_management', 'contact_management', 'sales_automation', 'reporting'],
            webhookSupport: true,
            rateLimits: { requests: 1000, window: '24h' },
            documentation: 'https://www.zoho.com/crm/developer-guide/'
        },
        {
            name: 'Microsoft Dynamics',
            type: 'CRM',
            status: 'active',
            description: 'Enterprise CRM and ERP solution',
            icon: 'dynamics-icon.svg',
            apiConfig: {
                baseUrl: process.env.REACT_APP_DYNAMICS_API_URL,
                timeout: 30000,
                version: 'v9.2',
                authType: 'oauth2'
            },
            features: ['customer_management', 'sales_automation', 'marketing_automation', 'service_management'],
            webhookSupport: true,
            rateLimits: { requests: 5000, window: '24h' },
            documentation: 'https://docs.microsoft.com/en-us/dynamics365/'
        },
        {
            name: 'Pipedrive',
            type: 'CRM',
            status: 'coming_soon',
            description: 'Sales CRM designed for salespeople',
            icon: 'pipedrive-icon.svg',
            apiConfig: {
                baseUrl: 'https://api.pipedrive.com',
                timeout: 30000,
                version: 'v1',
                authType: 'api_token'
            },
            features: ['deal_management', 'contact_management', 'activity_tracking', 'reporting'],
            webhookSupport: true,
            rateLimits: { requests: 100, window: '10s' },
            documentation: 'https://developers.pipedrive.com/docs'
        }
    ],

    HR: [
        {
            name: 'Active Directory',
            type: 'HR',
            status: 'active',
            description: 'Microsoft directory service for user management',
            icon: 'ad-icon.svg',
            apiConfig: {
                baseUrl: process.env.REACT_APP_AD_API_URL,
                timeout: 30000,
                version: 'v1.0',
                authType: 'service_principal'
            },
            features: ['user_management', 'group_management', 'authentication', 'sso'],
            webhookSupport: false,
            rateLimits: { requests: 1000, window: '24h' },
            documentation: 'https://docs.microsoft.com/en-us/azure/active-directory/develop/'
        },
        {
            name: 'Google Workspace',
            type: 'HR',
            status: 'active',
            description: 'Cloud-based productivity and collaboration tools',
            icon: 'google-workspace-icon.svg',
            apiConfig: {
                baseUrl: 'https://admin.googleapis.com',
                timeout: 30000,
                version: 'v1',
                authType: 'oauth2'
            },
            features: ['user_management', 'email_management', 'calendar_sync', 'document_sharing'],
            webhookSupport: true,
            rateLimits: { requests: 1000000, window: '24h' },
            documentation: 'https://developers.google.com/admin-sdk'
        },
        {
            name: 'Workday',
            type: 'HR',
            status: 'active',
            description: 'Human capital management platform',
            icon: 'workday-icon.svg',
            apiConfig: {
                baseUrl: process.env.REACT_APP_WORKDAY_API_URL,
                timeout: 30000,
                version: 'v40.1',
                authType: 'oauth2'
            },
            features: ['employee_management', 'payroll', 'benefits', 'recruitment'],
            webhookSupport: true,
            rateLimits: { requests: 1000, window: '24h' },
            documentation: 'https://doc.workday.com/'
        }
    ],

    EMAIL: [
        {
            name: 'Outlook Email Signature',
            type: 'EMAIL',
            status: 'active',
            description: 'Microsoft Outlook email signature integration',
            icon: 'outlook-icon.svg',
            apiConfig: {
                baseUrl: 'https://graph.microsoft.com',
                timeout: 30000,
                version: 'v1.0',
                authType: 'oauth2'
            },
            features: ['signature_management', 'template_creation', 'auto_deployment'],
            webhookSupport: false,
            rateLimits: { requests: 10000, window: '24h' },
            documentation: 'https://docs.microsoft.com/en-us/graph/'
        },
        {
            name: 'Gmail',
            type: 'EMAIL',
            status: 'active',
            description: 'Google Gmail integration for email management',
            icon: 'gmail-icon.svg',
            apiConfig: {
                baseUrl: 'https://gmail.googleapis.com',
                timeout: 30000,
                version: 'v1',
                authType: 'oauth2'
            },
            features: ['email_signature', 'contact_sync', 'calendar_integration'],
            webhookSupport: true,
            rateLimits: { requests: 1000000, window: '24h' },
            documentation: 'https://developers.google.com/gmail/api'
        }
    ],

    SAML: [
        {
            name: 'Azure SAML',
            type: 'SAML',
            status: 'active',
            description: 'Microsoft Azure Active Directory SAML integration',
            icon: 'azure-saml-icon.svg',
            apiConfig: {
                baseUrl: process.env.REACT_APP_AZURE_SAML_URL,
                timeout: 30000,
                version: 'v2.0',
                authType: 'saml'
            },
            features: ['single_sign_on', 'user_provisioning', 'role_based_access'],
            webhookSupport: false,
            rateLimits: { requests: 1000, window: '24h' },
            documentation: 'https://docs.microsoft.com/en-us/azure/active-directory/saml/'
        },
        {
            name: 'Okta SAML',
            type: 'SAML',
            status: 'active',
            description: 'Okta identity and access management SAML integration',
            icon: 'okta-icon.svg',
            apiConfig: {
                baseUrl: process.env.REACT_APP_OKTA_API_URL,
                timeout: 30000,
                version: 'v1',
                authType: 'saml'
            },
            features: ['single_sign_on', 'multi_factor_auth', 'user_management'],
            webhookSupport: true,
            rateLimits: { requests: 1000, window: '24h' },
            documentation: 'https://developer.okta.com/docs/'
        }
    ],

    CALENDAR: [
        {
            name: 'Chili Piper',
            type: 'CALENDAR',
            status: 'active',
            description: 'Intelligent meeting scheduling and routing platform',
            icon: 'chili-piper-icon.svg',
            apiConfig: {
                baseUrl: 'https://api.chilipiper.com',
                timeout: 30000,
                version: 'v1',
                authType: 'api_key'
            },
            features: ['meeting_scheduling', 'lead_routing', 'calendar_sync', 'analytics'],
            webhookSupport: true,
            rateLimits: { requests: 1000, window: '24h' },
            documentation: 'https://help.chilipiper.com/en/articles/'
        },
        {
            name: 'Calendly',
            type: 'CALENDAR',
            status: 'coming_soon',
            description: 'Online appointment scheduling software',
            icon: 'calendly-icon.svg',
            apiConfig: {
                baseUrl: 'https://api.calendly.com',
                timeout: 30000,
                version: 'v1',
                authType: 'api_key'
            },
            features: ['scheduling_links', 'calendar_integration', 'team_scheduling'],
            webhookSupport: true,
            rateLimits: { requests: 1000, window: '24h' },
            documentation: 'https://developer.calendly.com/api-docs/'
        }
    ],

    MARKETING: [
        {
            name: 'Marketo',
            type: 'MARKETING',
            status: 'active',
            description: 'Marketing automation platform',
            icon: 'marketo-icon.svg',
            apiConfig: {
                baseUrl: process.env.REACT_APP_MARKETO_API_URL,
                timeout: 30000,
                version: 'v1',
                authType: 'oauth2'
            },
            features: ['lead_management', 'email_marketing', 'campaign_automation', 'analytics'],
            webhookSupport: true,
            rateLimits: { requests: 100, window: '20s' },
            documentation: 'https://developers.marketo.com/rest-api/'
        },
        {
            name: 'Pardot',
            type: 'MARKETING',
            status: 'active',
            description: 'B2B marketing automation by Salesforce',
            icon: 'pardot-icon.svg',
            apiConfig: {
                baseUrl: 'https://pi.demo.pardot.com/api',
                timeout: 30000,
                version: 'v4',
                authType: 'api_key'
            },
            features: ['lead_scoring', 'email_marketing', 'campaign_tracking', 'reporting'],
            webhookSupport: true,
            rateLimits: { requests: 200, window: '24h' },
            documentation: 'https://developer.pardot.com/'
        }
    ]
};

// Helper functions for integration management
export const getIntegrationByType = (type) => {
    try {
        if (!INTEGRATIONS_CONFIG[type]) {
            throw new Error(`Integration type '${type}' not found`);
        }
        return INTEGRATIONS_CONFIG[type];
    } catch (error) {
        handleIntegrationError(error, `Getting integrations by type: ${type}`);
    }
};

export const getIntegrationByName = (name) => {
    try {
        for (const category of Object.values(INTEGRATIONS_CONFIG)) {
            const integration = category.find(item => item.name === name);
            if (integration) {
                return integration;
            }
        }
        throw new Error(`Integration '${name}' not found`);
    } catch (error) {
        handleIntegrationError(error, `Getting integration by name: ${name}`);
    }
};

export const getActiveIntegrations = () => {
    try {
        const activeIntegrations = [];

        for (const [type, integrations] of Object.entries(INTEGRATIONS_CONFIG)) {
            const active = integrations.filter(integration => integration.status === 'active');
            activeIntegrations.push(...active.map(integration => ({ ...integration, category: type })));
        }

        return activeIntegrations;
    } catch (error) {
        handleIntegrationError(error, 'Getting active integrations');
    }
};

export const getComingSoonIntegrations = () => {
    try {
        const comingSoonIntegrations = [];

        for (const [type, integrations] of Object.entries(INTEGRATIONS_CONFIG)) {
            const comingSoon = integrations.filter(integration => integration.status === 'coming_soon');
            comingSoonIntegrations.push(...comingSoon.map(integration => ({ ...integration, category: type })));
        }

        return comingSoonIntegrations;
    } catch (error) {
        handleIntegrationError(error, 'Getting coming soon integrations');
    }
};

export const validateIntegrationConfigExport = (config) => {
    try {
        return validateIntegrationConfigInternal(config);
    } catch (error) {
        handleIntegrationError(error, 'Validating integration config');
    }
};

export const getIntegrationApiConfig = (integrationName) => {
    try {
        const integration = getIntegrationByName(integrationName);
        if (!integration.apiConfig) {
            throw new Error(`No API configuration found for integration '${integrationName}'`);
        }
        return validateApiConfig(integration.apiConfig);
    } catch (error) {
        handleIntegrationError(error, `Getting API config for: ${integrationName}`);
    }
};

export const checkIntegrationStatus = (integrationName) => {
    try {
        const integration = getIntegrationByName(integrationName);
        return {
            name: integration.name,
            status: integration.status,
            isActive: integration.status === 'active',
            isComingSoon: integration.status === 'coming_soon',
            isDeprecated: integration.status === 'deprecated'
        };
    } catch (error) {
        handleIntegrationError(error, `Checking status for: ${integrationName}`);
    }
};

// Export the main configuration
export default INTEGRATIONS_CONFIG;